import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container, Typography, Paper, Box, Button, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Pagination, Avatar, Tooltip, InputAdornment
} from '@mui/material';
import {
  Chat, Search, Visibility, Refresh, Close, Send
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import api from '../services/api';
import Sidebar from '../components/Sidebar';

const ChatMessages = () => {
  const [customers, setCustomers] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [chatDialog, setChatDialog] = useState({ open: false, customer: null });
  const [refreshing, setRefreshing] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  // Create a ref for the dialog content
  const dialogContentRef = useRef(null);

  // Function to scroll dialog to bottom
const scrollToBottom = useCallback(() => {
  if (dialogContentRef.current) {
    dialogContentRef.current.scrollTo({
      top: dialogContentRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }
}, []);

  // Scroll to bottom when chatMessages change
  useEffect(() => {
    if (chatDialog.open) {
      // Use a small timeout to ensure DOM is updated
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [chatMessages, chatDialog.open, scrollToBottom]);

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(handler);
  }, [search]);

  // Fetch chats when page or debounced search changes
  useEffect(() => {
    fetchCustomerChats();
  }, [page, debouncedSearch]);

  const fetchCustomerChats = async () => {
    try {
      setLoading(true);
      const res = await api.get('/chat', {
        params: { page, search: debouncedSearch }
      });
      setCustomers(res.data.customers);
      setTotalPages(res.data.totalPages);
      setError('');
    } catch (err) {
      setError('Failed to fetch customer chats');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setPage(1);
    fetchCustomerChats();
  };

  const handleCustomerClick = async (customer) => {
    try {
      const res = await api.get(`/chat/customer/${customer._id}`);
      setChatMessages(res.data.messages);
      setChatDialog({ open: true, customer });
    } catch (err) {
      setError('Failed to fetch messages');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !chatDialog.customer) return;
    
    try {
      // In a real app, you would send this to your API
      const tempMessage = {
        _id: Date.now().toString(),
        content: { text: newMessage },
        direction: 'outbound',
        timestamp: new Date().toISOString()
      };
      
      setChatMessages(prev => [...prev, tempMessage]);
      setNewMessage('');
    } catch (err) {
      setError('Failed to send message');
    }
  };

  const handleCloseDialog = () => {
    setChatDialog({ open: false, customer: null });
    setNewMessage('');
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom>
            <Chat sx={{ mr: 1, verticalAlign: 'bottom' }} /> Chat Conversations
          </Typography>
          <Button 
            variant="outlined" 
            onClick={handleRefresh}
            startIcon={<Refresh />}
            disabled={refreshing}
          >
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ mb: 2 }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setError('')}
              >
                <Close fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}

        {/* Search */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <TextField
            fullWidth
            type="search"
            label="Search customers"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearch('')} edge="end">
                    <Close />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Paper>

        {/* Customers Table */}
        <Paper>
          {loading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell>Last Message</TableCell>
                      <TableCell>Unread</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customers.length > 0 ? (
                      customers.map((cust) => (
                        <TableRow key={cust._id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                                {cust.name.charAt(0)}
                              </Avatar>
                              <Box>
                                <Typography fontWeight="bold">{cust.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {cust.phone}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {cust.lastMessage?.text || '[no message]'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {cust.lastMessage?.timestamp &&
                                format(parseISO(cust.lastMessage.timestamp), 'MMM dd, HH:mm')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {cust.unreadCount > 0 && (
                              <Chip 
                                label={`${cust.unreadCount} unread`} 
                                color="warning" 
                                size="small" 
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="View Chat">
                              <IconButton 
                                onClick={() => handleCustomerClick(cust)}
                                color="primary"
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">
                            No customers found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" p={2}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(e, val) => setPage(val)}
                    color="primary"
                  />
                </Box>
              )}
            </>
          )}
        </Paper>

        {/* Chat Dialog */}
        <Dialog
          open={chatDialog.open}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { height: '80vh' }
          }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              Chat with {chatDialog.customer?.name}
              {chatDialog.customer?.phone && (
                <Typography variant="caption" display="block" color="text.secondary">
                  {chatDialog.customer.phone}
                </Typography>
              )}
            </Box>
            <IconButton onClick={handleCloseDialog}>
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent
            ref={dialogContentRef}
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              bgcolor: '#f5f5f5', 
              p: 2,
              overflow: 'auto'
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              {chatMessages.map((msg) => (
                <Box
                  key={msg._id}
                  display="flex"
                  justifyContent={msg.direction === 'inbound' ? 'flex-start' : 'flex-end'}
                  mb={1}
                >
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      maxWidth: '70%',
                      bgcolor: msg.direction === 'inbound' ? 'grey.300' : 'primary.main',
                      color: msg.direction === 'inbound' ? 'black' : 'white'
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                      {msg.content?.text || '[no content]'}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5 }}>
                      {format(parseISO(msg.timestamp), 'HH:mm')}
                    </Typography>
                  </Box>
                </Box>
              ))}
              <div ref={messagesEndRef} />
            </Box>
            
            {/* Message Input */}
            {/* <Box sx={{ display: 'flex', mt: 2 }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                multiline
                maxRows={3}
                sx={{ mr: 1 }}
              />
              <Button
                variant="contained"
                onClick={handleSendMessage}
                disabled={!newMessage.trim()}
                sx={{ minWidth: 'auto', px: 2 }}
              >
                <Send />
              </Button>
            </Box> */}
          </DialogContent>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ChatMessages;